import {
  Application,
  Assets,
  Container,
  Graphics,
  Rectangle,
  Renderer,
  RenderTexture,
  Sprite,
} from 'pixi.js'
import { buildNumberingOverlay, buildTimestampOverlay } from './items'
import { Side, Section, ImageLayer, TextLayer, DragManager } from './pixi-impl'
import { Logo } from '../eizot/types'
import { ItemConfig } from '../print/config/types'
import { Viewport } from 'pixi-viewport'

export interface Color {
  name: string
  uses_dark_logos: boolean
}

export interface LayerData {
  logo: Logo
  offset_x: number
  offset_y: number
  scale: number
  display: 'front' | 'back' | 'both'
}

export interface TimestampData {
  font: FontData
  format: string
  font_size: number
}

export interface NumberingData {
  format: string
  text_font: { family: string; url: string }
  text_font_size: number
  number_font: { family: string; url: string }
  number_font_size: number
}

export interface FontData {
  family: string
  url: string
}

export interface PreviewData {
  image: string
  color: Color
  side: string
  layers: LayerData[]
  composite_operation?: string
  timestamp?: TimestampData
  numbering?: NumberingData
}

// !! ~Only apply mask to Main Layer~ -> mask can move -> mask is layer and masks lower layers
// -- rename files
// -- support in processing side
// -- remove all old files
// -- de-react
export class EizoTPreview {
  private app: Application
  private root: Viewport
  private config: ItemConfig
  private sides: Record<string, Side>
  private sections: Record<string, Section>
  private colorResolver: (item: string, color: string) => Record<string, string>
  private printSide: string
  private timestampLayer: TextLayer | null
  private numberingLayer: TextLayer | null
  private color: Color
  private _currentSize: string
  private _sizes: Map<
    string,
    {
      mainLayer: ImageLayer | null
      backLayer: ImageLayer | null
      logos: Map<ImageLayer, [Logo, string]>
      masks: Record<string, [Logo, ImageLayer]>
    }
  >
  private dm: DragManager
  private inited = false
  private renderedSide: string

  public constructor(
    config: ItemConfig,
    colorResolver: (item: string, color: string) => Record<string, string>,
    includeSides?: string[]
  ) {
    this.app = new Application()
    this.dm = new DragManager(new Container())
    const { sections, sides } = buildLayout(config, this.dm, includeSides)
    this.config = config
    this.sections = sections
    this.sides = sides
    this.printSide = config.printable_sections[0]
    this.timestampLayer = null
    this.numberingLayer = null
    this.colorResolver = colorResolver
    this._sizes = new Map()
    this.setColor(this.config.colors[0].system_id)
    this.setSize(config.sizes[0])
    this.renderedSide = Object.keys(sides)[0]
  }

  public get mainLayer() {
    return this._sizes.get(this._currentSize)?.mainLayer
  }

  public get backLayer() {
    return this._sizes.get(this._currentSize)?.backLayer
  }

  public get layers() {
    const size = this._sizes.get(this._currentSize)
    if (size == null) return []
    return Array.from(size.logos.entries())
  }

  public get currentSize() {
    return this._currentSize
  }

  public get timestamp() {
    return this.timestampLayer
  }

  public get numbering() {
    return this.numberingLayer
  }

  public get sizes() {
    return Array.from(this._sizes.entries())
  }

  public get masks() {
    const size = this._sizes.get(this._currentSize)
    if (size == null) return {}
    return size.masks
  }

  public getConfig() {
    return this.config
  }

  public setColor(color: string, uses_dark = false) {
    this.color = {
      name: color,
      uses_dark_logos: uses_dark,
    }

    const bgs = this.colorResolver(this.config.vendor_code, color)
    for (const key of Object.keys(bgs)) {
      this.sides[key]?.setBaseImage(bgs[key])
    }

    this._sizes.forEach((size) => {
      size.logos.forEach((layer, logo) => {
        const url = uses_dark ? layer[0].dark_image : layer[0].light_image
        logo.setSource(url)
      })
    })

    this.timestampLayer?.setColor(uses_dark ? '#000000' : '#ffffff')
    this.numberingLayer?.setColor(uses_dark ? '#000000' : '#ffffff')
  }

  public blend(on: boolean) {
    const mode = on ? 'multiply' : 'source-over'
    Object.entries(this.sides).forEach(([name, side]) => {
      side.setCompositeOperation(mode)
    })
  }

  public setPrintSide(side: string) {
    if (this.mainLayer == null) return

    if (side == 'front') {
      side = this.config.printable_sections[0]
    } else if (side == 'back') {
      side = this.config.printable_sections[1]
    }

    if (this.config.printable_sections.some((n) => n === side)) {
      this.printSide = side
      this.removeLayer(this.mainLayer)
      this.removeLayer(this.backLayer)
      if (side === '2' || side === '105') {
        this.sections[side].addLayer(this.backLayer, true, 0)
        Object.values(this.sides).forEach((s) => s.setPrintSideDisplay('back'))
      } else {
        this.sections[side].addLayer(this.mainLayer, true, 0)
        Object.values(this.sides).forEach((s) => s.setPrintSideDisplay('front'))
      }
    } else {
      console.warn('Tried to set non-printable side')
    }
  }

  public addSize(size: string) {
    if (this._sizes.has(size)) return
    const mainLayer = this.mainLayer?.clone()
    const backLayer = this.backLayer?.clone()
    this._sizes.set(size, {
      mainLayer: mainLayer ?? null,
      backLayer: backLayer ?? null,
      logos: new Map(),
      masks: {},
    })
  }

  public removeSize(size: string) {
    if (!this._sizes.has(size)) return false
    if (this.sizes.length === 1) return false

    if (this._currentSize === size) {
      const nextSize = Array.from(this._sizes.keys()).filter(
        (n) => n !== size
      )[0]
      this.setSize(nextSize)
    }
    this._sizes.delete(size)
    return true
  }

  public setSize(size: string, skipScale = false) {
    this._currentSize = size
    if (!skipScale) {
      Object.entries(this.sides).forEach(([name, side]) => {
        const config = this.config.sides.find((n) => n.name === name)
        const scale = config.sizes[size] ?? 1
        side.setBaseImageScale(scale)
      })
    }

    this._sizes.forEach((size) => {
      if (size.mainLayer != null) {
        this.removeLayer(size.mainLayer)
      }
      if (size.backLayer != null) {
        this.removeLayer(size.backLayer)
      }

      size.logos.forEach(([, side], layer) => {
        this.sections[side].removeLayer(layer)
      })

      Object.keys(size.masks).forEach((side) => {
        this.sections[side]?.setMask(null)
      })
    })

    const newSize = this._sizes.get(size)
    if (newSize == null) return

    const frontSide = this.config.printable_sections[0]
    const backSide = this.config.printable_sections[1]

    if (newSize.mainLayer != null && this.printSide === frontSide) {
      this.sections[this.printSide].addLayer(newSize.mainLayer, true, 0)
    }

    if (newSize.backLayer != null && this.printSide === backSide) {
      this.sections[this.printSide].addLayer(newSize.backLayer, true, 0)
    }

    newSize.logos.forEach(([, side], layer) => {
      this.sections[side].addLayer(layer)
    })

    Object.entries(newSize.masks).forEach(([side, [_logo, layer]]) => {
      this.setMask(side, layer)
    })
  }

  public copySize(from: string, to: string) {
    if (from === to) {
      return false
    }

    const fromSize = this._sizes.get(from)
    const toSize = this._sizes.get(to)

    if (fromSize == null || toSize == null) {
      return false
    }

    if (this._currentSize === to) {
      this.removeLayer(toSize.mainLayer)
      this.removeLayer(toSize.backLayer)
      toSize.logos.forEach((_, layer) => {
        this.removeLayer(layer)
      })
    }

    toSize.mainLayer = fromSize.mainLayer.clone()
    toSize.backLayer = fromSize.backLayer.clone()
    toSize.logos.clear()

    for (const [side, [logo, image]] of Object.entries(fromSize.masks)) {
      toSize.masks[side] = [{ ...logo }, image.clone()]
    }

    for (const [layer, side] of Array.from(fromSize.logos.entries())) {
      const cloned = layer.clone()
      toSize.logos.set(cloned, side)
    }

    if (this._currentSize === to) {
      this.setSize(to)
    }

    return true
  }

  public async setMainLayer(data: {
    url: string
    top: number
    left: number
    scale: number
    side: string
  }) {
    if (this.mainLayer != null) {
      this.removeLayer(this.mainLayer)
    }

    const size = this._sizes.get(this._currentSize)
    if (size != null) {
      const image = document.createElement('img')
      image.src = data.url
      await new Promise((resolve) => (image.onload = resolve))
      await Assets.load({ src: image.src, loadParser: 'loadTextures' })
      size.mainLayer = this.sections[data.side].addImage(image, true, 0)
      size.mainLayer.alterTransform({
        y: data.top,
        x: data.left,
        scaleX: data.scale,
        scaleY: data.scale,
      })
    }
    this.removeLayer(size.backLayer)

    return size?.mainLayer
  }

  public async setBackLayer(data: {
    url: string
    top: number
    left: number
    scale: number
    side: string
  }) {
    if (this.backLayer != null) {
      this.removeLayer(this.backLayer)
    }

    const size = this._sizes.get(this._currentSize)
    if (size != null) {
      const image = document.createElement('img')
      image.src = data.url
      await new Promise((resolve) => (image.onload = resolve))
      await Assets.load({ src: image.src, loadParser: 'loadTextures' })
      size.backLayer = this.sections[data.side].addImage(image, true)
      size.backLayer.alterTransform({
        y: data.top,
        x: data.left,
        scaleX: data.scale,
        scaleY: data.scale,
      })
    }
    this.removeLayer(size.mainLayer)

    return size?.backLayer
  }

  public async setMask(side: string, data: LayerData | ImageLayer | null) {
    const section = this.sections[side]
    if (data instanceof ImageLayer) {
      return section.setMask(data)
    }

    if (data != null) {
      const image = document.createElement('img')
      image.src = data.logo.light_image
      await Assets.load({ src: image.src, loadParser: 'loadTextures' })
      const layer = section.setMask(image)
      layer.alterTransform({
        x: data.offset_x,
        y: data.offset_y,
        scaleX: data.scale,
        scaleY: data.scale,
      })
      this._sizes.get(this._currentSize).masks[side] = [data.logo, layer]
      return layer
    }

    delete this._sizes.get(this._currentSize).masks[side]
    section.setMask(null)
    return null
  }

  public async addLogo(side: string, data: LayerData) {
    const section = this.sections[side]
    const image = document.createElement('img')
    image.src = this.color.uses_dark_logos
      ? data.logo.dark_image
      : data.logo.light_image
    await Assets.load({ src: image.src, loadParser: 'loadTextures' })
    const layer = section.addImage(image)
    layer.alterTransform({
      x: data.offset_x,
      y: data.offset_y,
      scaleX: data.scale,
      scaleY: data.scale,
    })
    layer.setDisplay(data.display)

    const size = this._sizes.get(this._currentSize)
    if (size != null) {
      size.logos.set(layer, [data.logo, side])
    }

    return layer
  }

  public removeLayer(layer: TextLayer | ImageLayer): Section | null {
    this._sizes.forEach((size) => {
      size.logos.delete(layer as ImageLayer)
    })

    const removedSection = Object.values(this.sections).find((section) =>
      section.removeLayer(layer)
    )
    return removedSection ?? null
  }

  public async setTimestamp(side: string, data: TimestampData) {
    await loadFont(data.font)

    const resolved = buildTimestampOverlay(data, this.color.uses_dark_logos)
    if (this.timestampLayer != null) {
      const previousSection = this.removeLayer(this.timestampLayer)
      this.timestampLayer.setText(resolved.content)
      this.timestampLayer.setFont(resolved.font)
      this.timestampLayer.setFontSize(resolved.font_size)

      const newSection = this.sections[side]
      if (previousSection !== newSection) {
        this.timestampLayer.reset()
      }
      newSection.addLayer(this.timestampLayer)
    } else {
      this.timestampLayer = this.sections[side].addText(resolved.content)
      this.timestampLayer.setFontSize(resolved.font_size)
      this.timestampLayer.setFont(resolved.font)
    }

    return this.timestampLayer
  }

  public removeTimestamp() {
    if (this.timestampLayer != null) {
      this.removeLayer(this.timestampLayer)
    }
  }

  public async setNumbering(side: string, data: NumberingData) {
    await Promise.all([loadFont(data.text_font), loadFont(data.number_font)])

    const resolved = buildNumberingOverlay(data, this.color.uses_dark_logos)
    if (this.numberingLayer != null) {
      const previousSection = this.removeLayer(this.numberingLayer)
      this.numberingLayer.setText(resolved.content)
      this.numberingLayer.setFont(resolved.font)
      this.numberingLayer.setFontSize(resolved.font_size)
      this.numberingLayer.setColor(resolved.fill)
      //TODO:this.numberingLayer.setStyle(resolved.styles)
      const newSection = this.sections[side]
      if (previousSection !== newSection) {
        this.numberingLayer.reset()
      }
      newSection.addLayer(this.numberingLayer)
    } else {
      this.numberingLayer = this.sections[side].addText(resolved.content)
      this.numberingLayer.setText(resolved.content)
      this.numberingLayer.setFont(resolved.font)
      this.numberingLayer.setFontSize(resolved.font_size)
      this.numberingLayer.setColor(resolved.fill)
      //TODO:this.numberingLayer.setStyle(resolved.styles)
    }

    return this.numberingLayer
  }

  public removeNumbering() {
    if (this.numberingLayer != null) {
      this.removeLayer(this.numberingLayer)
    }
  }

  public showGuides(show = true) {
    for (const side of Object.values(this.sides)) {
      side.showOutlines(show)
    }
  }

  public availableSides() {
    return Object.keys(this.sides)
  }

  public async snapshot(sideName: string = this.renderedSide): Promise<string> {
    const side = this.sides[sideName]
    if (side == null) return null
    this.app.stop()
    const renderTexture = this.app.renderer.generateTexture(side.container)
    const data = await this.app.renderer.extract.base64(renderTexture)
    this.app.start()
    return data
  }

  private cleanup: (() => void) | null = null
  public showSide(sideName: string) {
    const side = this.sides[sideName]
    if (side == null) return
    this.root.removeChild(this.sides[this.renderedSide].container)
    this.app.stage.removeChild(this.sides[this.renderedSide].overlay)
    this.root.addChildAt(side.container, 0)
    this.app.stage.addChildAt(side.overlay, 1)
    this.cleanup?.()
    const ticks = () => {
      for (let i = 0; i < side.overlay.children.length; i++) {
        ;(side.overlay.children[i] as any).tick?.()
      }
    }
    this.app.ticker.add(ticks)
    this.cleanup = () => {
      this.app.ticker.remove(ticks)
    }
    // addjust viewport zoom and position instead of container
    const scale = this.app.screen.height / side.height
    this.root.moveCenter(side.container.width / 2, side.container.height / 2)
    this.root.setZoom(scale, true)
    this.renderedSide = sideName
  }

  private appInited = false
  public async start(to: HTMLElement) {
    if (!this.inited && !this.appInited) {
      await this.app.init({ background: 'white', resizeTo: to })
      this.appInited = true

      this.root = new Viewport({
        passiveWheel: false,
        events: this.app.renderer.events,
        screenWidth: this.app.screen.width,
        screenHeight: this.app.screen.height,
      })

      this.root
        .drag({ keyToPress: ['AltLeft', 'AltRight', 'ShiftLeft'] })
        .wheel({ keyToPress: ['AltLeft', 'AltRight', 'ShiftLeft'] })

      this.app.stage.sortableChildren = true
      this.app.ticker.maxFPS = 24

      this.app.stage.addChild(this.root)
      this.app.stage.eventMode = 'static'
      this.app.stage.hitArea = this.app.screen
      this.dm.setRoot(this.app.stage)
      this.showSide(this.renderedSide)

      const d =
        this.app.screen.height / Math.max(Object.keys(this.sides).length, 4)
      const h = d * Object.keys(this.sides).length
      const prevContainer = new Container({ isRenderGroup: true })
      prevContainer.position.set(0, 0)
      prevContainer.hitArea = new Rectangle(0, 0, d, h)
      prevContainer.addChild(new Graphics().rect(0, 0, d, h).fill('white'))
      this.app.stage.addChild(prevContainer)

      const sideSprites: MirrorSprite[] = []
      let i = 0
      Object.entries(this.sides).forEach(([name, side]) => {
        const o_sprite = new MirrorSprite(
          side.container,
          side.width,
          side.height
        )
        o_sprite.eventMode = 'static'
        o_sprite.on('click', () => {
          this.showSide(name)
        })
        prevContainer.addChild(o_sprite)
        o_sprite.position.set(0, i * d)
        o_sprite.scale.set(d / o_sprite.height)
        const diff = d - o_sprite.width
        o_sprite.position.x = Math.floor(diff / 2)
        sideSprites.push(o_sprite)

        i++
      })

      let prevRnder: number | null = null
      this.app.ticker.add(() => {
        if (prevRnder != null && Date.now() - prevRnder < 2000) return
        prevRnder = Date.now()

        for (let i = 0; i < sideSprites.length; i++) {
          sideSprites[i].renderSwap(this.app.renderer)
        }
      })

      this.inited = true
    }
    to.appendChild(this.app.canvas)
    this.app.start()
  }

  public async renderTo(to: Record<string, HTMLCanvasElement>) {
    if (!this.inited) {
      this.inited = true
      await this.app.init({ autoStart: false, background: 'white' })
      this.app.renderer.resize(600, 600)
    }

    for (const [key, value] of Object.entries(to)) {
      const side = this.sides[key]
      if (side == null) continue
      await side.bg_image
      await side.ensureAssets()
      this.app.stage.removeChildren()
      this.app.stage.addChild(side.container)
      const scale = this.app.screen.height / side.height
      side.container.scale.set(scale)
      this.app.render()
      value.getContext('2d').drawImage(this.app.canvas, 0, 0)
    }
  }

  public destroy() {
    this.app.stop()
    this.inited = false
  }
}

class MirrorSprite extends Sprite {
  private target: Container
  private dtexture: RenderTexture
  private dbuffer: RenderTexture

  public constructor(target: Container, width: number, height: number) {
    const dtexture = RenderTexture.create({ width, height })
    super(dtexture)
    this.target = target
    this.dbuffer = RenderTexture.create({ width, height })
    this.dtexture = dtexture
  }

  public renderSwap(renderer: Renderer) {
    const temp = this.dtexture
    this.texture = this.dbuffer
    this.dbuffer = temp
    renderer.render({ container: this.target, target: this.dbuffer })
  }
}

async function loadFont(data: FontData) {
  Assets.add({ src: data.url, alias: data.family })
  await Assets.load(data.family)
}

function buildLayout(
  config: ItemConfig,
  dm: DragManager,
  includedSides?: string[]
) {
  const sides: Record<string, Side> = {}
  const sections: Record<string, Section> = {}

  for (const conf of config.sections) {
    const section = new Section(conf.name, conf.config)
    sections[conf.id.toString()] = section
  }

  const included =
    includedSides == null
      ? config.sides
      : config.sides.filter((side) => includedSides.includes(side.name))

  for (const side_conf of included) {
    const side = new Side(
      side_conf.width,
      side_conf.height,
      side_conf.origin,
      dm
    )

    for (const section_conf of side_conf.sections) {
      const section = sections[section_conf.id.toString()]
      if (section != null) {
        side.addSection(section, section_conf.offset, section_conf.clip)
      }
    }

    sides[side_conf.name] = side
  }

  return { sections, sides }
}
